import './styles.scss';

import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';
import { useState } from 'react';

interface RevealSecretProps {
  secret: string;
  /** Field label shown before the (masked) value. */
  label?: string;
  /**
   * Show the one-time "save this now" warning. The backend returns a plaintext
   * secret only at creation (and on a future Regenerate), never on read, so this
   * is effectively always true today.
   */
  showWarning?: boolean;
}

const MASK = '****************';

/** How long the Copy button holds its "Copied"/"Copy failed" feedback. */
const COPY_FEEDBACK_MS = 2000;

/**
 * Displays a secret with a Show/Hide toggle and a Copy button. Deliberately
 * decoupled from the create flow so it can be reused by a future per-row
 * "Regenerate" action, which mints a new key and returns a fresh one-time
 * secret (see IM-2168 follow-up). The backend never re-serves an existing
 * secret - storage is one-way encrypted - so re-view is not a supported path.
 */
const RevealSecret = ({ secret, label = 'Secret', showWarning = true }: RevealSecretProps) => {
  const [visible, setVisible] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopyStatus('copied');
    } catch (error) {
      // Clipboard can be blocked by browser permissions; surface, do not crash.
      console.error('Failed to copy secret to clipboard', error);
      setCopyStatus('failed');
    } finally {
      setTimeout(() => setCopyStatus('idle'), COPY_FEEDBACK_MS);
    }
  };

  const copyLabel =
    copyStatus === 'copied' ? 'Copied' : copyStatus === 'failed' ? 'Copy failed' : 'Copy';

  return (
    <div className="reveal-secret">
      <div className="reveal-secret-row">
        <span className="reveal-secret-label">{label}</span>
        <code className="reveal-secret-value" data-openreplay-obscured>
          {visible ? secret : MASK}
        </code>
        <div className="reveal-secret-actions">
          <Button className="reveal-secret-toggle" onClick={() => setVisible((prev) => !prev)}>
            {visible ? 'Hide' : 'Show'}
          </Button>
          <Button className="reveal-secret-copy" onClick={handleCopy}>
            {copyLabel}
          </Button>
        </div>
      </div>

      {showWarning && (
        <div className="reveal-secret-warning">
          <div className="reveal-secret-warning-header">
            <FontAwesomeIcon icon={faTriangleExclamation} className="reveal-secret-warning-icon" />
            <span>Save this now - it will not be shown again.</span>
          </div>
          <p>Copy the secret before closing this window. It cannot be retrieved later.</p>
        </div>
      )}
    </div>
  );
};

export default RevealSecret;
